import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BrandedEntry() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, [slug]);

  const fetchBranding = async () => {
    try {
      const res = await fetch(`${API_URL}/api/company/branding/${slug}`);
      if (!res.ok) throw new Error('Entreprise non trouvée');
      const data = await res.json();
      setBranding(data);
    } catch (e) {
      setError('Entreprise non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Identifiants incorrects');
      }
      // Check that user belongs to this company
      if (branding && data.user?.company_id !== branding.company_id) {
        throw new Error("Ce compte n'est pas associé à cette entreprise");
      }
      localStorage.setItem('user_token', data.access_token);
      localStorage.setItem('user_info', JSON.stringify(data.user));
      localStorage.setItem('company_branding', JSON.stringify(branding));
      toast.success(`Bienvenue ${data.user.name} !`);
      navigate('/form');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <h2 className="text-2xl font-bold text-foreground mb-4">Entreprise non trouvée</h2>
        <p className="text-foreground/60 mb-6">Le lien que vous avez utilisé n'est pas valide.</p>
        <button onClick={() => navigate('/')} className="px-6 py-3 bg-accent text-accent-foreground rounded-md">
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const b = branding?.branding || {};
  const primaryColor = b.primary_color || '#f97316';
  const headerColor = b.header_color || '#ffffff';
  const bgColor = b.background_color || '#f8fafc';
  const bgImage = b.background_image_url ? `${API_URL}${b.background_image_url}` : '';
  const logoUrl = b.logo_url ? `${API_URL}${b.logo_url}` : '';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: bgColor,
        backgroundImage: bgImage ? `url(${bgImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Header */}
      <header
        className="h-14 sm:h-16 border-b backdrop-blur flex items-center justify-between px-4 sm:px-6"
        style={{ backgroundColor: headerColor + 'ee' }}
      >
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-8 sm:h-10 object-contain" />
          ) : (
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: primaryColor }}>
              {b.title || branding?.company_name || 'Litige-Protect'}
            </h1>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div
          className="w-full max-w-md space-y-6 bg-white/95 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-xl"
        >
          {/* Logo + Title */}
          <div className="text-center space-y-3">
            {logoUrl && (
              <img src={logoUrl} alt="Logo" className="h-16 sm:h-20 object-contain mx-auto mb-2" />
            )}
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {b.title || branding?.company_name}
            </h2>
            {b.description && (
              <p className="text-sm sm:text-base text-foreground/60">{b.description}</p>
            )}
            {!b.description && (
              <p className="text-sm text-foreground/60">Connectez-vous pour accéder aux formulaires</p>
            )}
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                <input
                  data-testid="branded-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.fr"
                  className="w-full h-12 bg-white border border-input rounded-lg pl-10 pr-4 focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': primaryColor, borderColor: email ? primaryColor : undefined }}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                <input
                  data-testid="branded-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mot de passe"
                  className="w-full h-12 bg-white border border-input rounded-lg pl-10 pr-12 focus:ring-2 focus:outline-none"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              data-testid="branded-login-submit"
              type="submit"
              disabled={loginLoading}
              className="w-full h-12 sm:h-14 text-white text-base sm:text-lg font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              {loginLoading ? 'Connexion...' : (
                <>Se connecter <ArrowRight className="w-5 h-5" /></>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-foreground/40">
            Propulsé par Litige-Protect
          </p>
        </div>
      </main>
    </div>
  );
}
